import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import {
  SyncOutlined as SyncOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HourglassEmptyOutlined as HourglassIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  ReplayOutlined as RetryIcon,
} from '@mui/icons-material';
import StatCard from '../../Components/GatewayContagem/StatCard';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import IngestionRunsTable from '../../Components/GatewayContagem/IngestionRunsTable';
import { ingestionAPI } from '../../API/ingestionAPI';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Azure Function cron "0 */15 * * * *" fires at :00, :15, :30, :45.
const minutesUntilFunctionFire = () => {
  const now = new Date();
  const totalSeconds = now.getMinutes() * 60 + now.getSeconds();
  const intervalSeconds = 15 * 60;
  return Math.ceil((intervalSeconds - (totalSeconds % intervalSeconds)) / 60);
};

// Background service: PeriodicTimer fires every N min from process start.
const parseUtcDate = (iso) => {
  if (!iso) return null;
  const str = String(iso);
  if (str.endsWith('Z') || str.includes('+') || (str.includes('-') && str.lastIndexOf('-') > 10)) {
    return new Date(str);
  }
  return new Date(`${str}Z`);
};

// Best approximation: lastStartedAt + intervalMinutes.
const minutesUntilBackgroundFire = (lastStartedAt, intervalMinutes) => {
  const d = parseUtcDate(lastStartedAt);
  if (!d || isNaN(d.getTime())) return intervalMinutes;
  const nextMs = d.getTime() + intervalMinutes * 60000;
  return Math.max(0, Math.ceil((nextMs - Date.now()) / 60000));
};

const formatLastRun = (iso, t) => {
  const d = parseUtcDate(iso);
  if (!d || isNaN(d.getTime())) return t('header.never');
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return t('header.justNow');
  return t('header.minutesAgo', { count: mins });
};

// ─── Header info chip ─────────────────────────────────────────────────────────

const IngestionTimingInfo = ({ lastStartedAt, functionEnabled, backgroundServiceEnabled, ingestionIntervalMinutes }) => {
  const { t } = useTranslation('ingestion');
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const showNext = functionEnabled || backgroundServiceEnabled;
  let minsUntilNext = null;

  if (backgroundServiceEnabled) {
    minsUntilNext = minutesUntilBackgroundFire(lastStartedAt, ingestionIntervalMinutes);
  } else if (functionEnabled) {
    minsUntilNext = minutesUntilFunctionFire();
  }

  const nextLabel = minsUntilNext === null ? null
    : minsUntilNext < 1
      ? t('header.inLessThanMinute')
      : t('header.inMinutes', { count: minsUntilNext });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: '5px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1, whiteSpace: 'nowrap' }}>
          {t('header.last')}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', lineHeight: 1, whiteSpace: 'nowrap' }}>
          {formatLastRun(lastStartedAt, t)}
        </Typography>
      </Box>
      {showNext && (
        <>
          <Box sx={{ width: '1px', height: 32, bgcolor: 'divider', flexShrink: 0 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1, whiteSpace: 'nowrap' }}>
              {t('header.next')}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', lineHeight: 1, whiteSpace: 'nowrap' }}>
              {nextLabel}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const IngestionMonitorPage = () => {
  const { t } = useTranslation('ingestion');

  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [runCounts, setRunCounts] = useState({ success: 0, partial: 0, inProgress: 0 });
  const [loading, setLoading] = useState(true);
  const [scheduleStatus, setScheduleStatus] = useState({ functionEnabled: false, backgroundServiceEnabled: false, ingestionIntervalMinutes: 15 });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ingestionAPI.listRuns({ page: page + 1, itemsPerPage: rowsPerPage });
      if (!res || res.error) {
        toast.error(t('feedback.errorLoading'));
        setRuns([]);
        setTotal(0);
        return;
      }
      setRuns(res.value?.items ?? []);
      setTotal(res.value?.totalCount ?? 0);
      setRunCounts({
        success: res.value?.successCount ?? 0,
        partial: res.value?.partialCount ?? 0,
        inProgress: res.value?.inProgressCount ?? 0,
      });
    } catch {
      toast.error(t('feedback.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  useEffect(() => {
    ingestionAPI.getStatus().then((res) => {
      if (res && !res.error) {
        setScheduleStatus({
          functionEnabled: res.functionEnabled ?? false,
          backgroundServiceEnabled: res.backgroundServiceEnabled ?? false,
          ingestionIntervalMinutes: res.ingestionIntervalMinutes ?? 15,
        });
      }
    });
  }, []);

  const stats = useMemo(() => ({
    total,
    success: runCounts.success,
    partial: runCounts.partial,
    inProgress: runCounts.inProgress,
  }), [total, runCounts]);

  const lastStartedAt = runs[0]?.startedAt ?? null;

  const handleRetry = useCallback(async (runId) => {
    const res = await ingestionAPI.retryRun(runId);
    if (!res || res.error) {
      if (res?.statusCode === 409) toast.error(t('feedback.retryConflict'));
      else toast.error(t('feedback.retryError'));
    } else {
      toast.success(t('feedback.retryStarted'));
      setTimeout(loadRuns, 2000);
    }
  }, [loadRuns, t]);

  const retryableRuns = runs.filter(
    (r) => !r.isRetried &&
      ((r.completedAt && r.isPartial) ||
        (!r.completedAt && Date.now() - new Date(r.startedAt).getTime() > 5 * 60 * 1000))
  );

  const handleRetryAll = useCallback(async () => {
    for (const run of retryableRuns) {
      const res = await ingestionAPI.retryRun(run.id);
      if (!res || res.error) break;
    }
    toast.success(t('feedback.retryAllDone', { count: retryableRuns.length }));
    setTimeout(loadRuns, 2000);
  }, [retryableRuns, loadRuns, t]);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title={t('title')} subtitle={t('subtitle')}>
        <IngestionTimingInfo
          lastStartedAt={lastStartedAt}
          functionEnabled={scheduleStatus.functionEnabled}
          backgroundServiceEnabled={scheduleStatus.backgroundServiceEnabled}
          ingestionIntervalMinutes={scheduleStatus.ingestionIntervalMinutes}
        />
        {retryableRuns.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RetryIcon sx={{ fontSize: 16 }} />}
            onClick={handleRetryAll}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
          >
            {t('actions.retryAll')}
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<SyncOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={loadRuns}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}
        >
          {t('actions.refresh')}
        </Button>
      </PageHeader>

      {/* ── KPI cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.total')}
            value={stats.total}
            icon={<HourglassIcon fontSize="inherit" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.success')}
            value={stats.success}
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            accentColor="#2E9E5B"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.partial')}
            value={stats.partial}
            icon={<WarningAmberOutlinedIcon fontSize="inherit" />}
            accentColor="#E0A800"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('kpi.inProgress')}
            value={stats.inProgress}
            icon={<ErrorOutlineIcon fontSize="inherit" />}
            accentColor="#1B75BB"
            loading={loading}
          />
        </Grid>
      </Grid>

      <IngestionRunsTable
        runs={runs}
        totalCount={total}
        page={page}
        rowsPerPage={rowsPerPage}
        loading={loading}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        onRetry={handleRetry}
      />
    </Box>
  );
};

export default IngestionMonitorPage;
